import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, Edit, RotateCw } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { WorkflowsFilter } from "../filter/filter";
import { useGetAllWorkflows, useReRunWorkflow } from "../../../hooks";

interface WorkflowData {
  id: string;
  workflowId: string;
  workflowName: string;
  audienceType: string;
  actionType: string;
  status: string;
  createdBy: string;
  createdOn: string;
}

interface WorkflowFilters {
  audienceType: string;
  actionType: string;
  status: string;
}

const WorkflowsList: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read all params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const audienceType = searchParams.get("audienceType") || "";
  const actionType = searchParams.get("actionType") || "";
  const status = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const reRunWorkflow = useReRunWorkflow();

  const { data: workflowData } = useGetAllWorkflows(
    currentPage,
    PAGE_SIZE,
    searchQuery,
    audienceType,
    actionType,
    status
  );

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const audienceTypeList = [
    { label: "Commercial Customers", value: "commercial" },
    { label: "Residential Customers", value: "residential" },
    { label: "Business Users", value: "business_users" },
    { label: "Field Workers", value: "field_workers" },
  ];

  const actionTypeList = [
    { label: "Immediate", value: "immediate" },
    { label: "Time-Based", value: "time_based" },
    { label: "Scheduled", value: "scheduled" },
  ];

  const statusList = [
    { label: "Active", value: "active" },
    { label: "Completed", value: "completed" },
    { label: "Paused", value: "paused" },
    { label: "Failed", value: "failed" },
  ];


  // Update pagination
  useEffect(() => {
    const totalItems = workflowData?.count;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [workflowData, currentPage]);

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.audienceType;
    delete newParams.actionType;
    delete newParams.status;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(audienceType && { audienceType }),
      ...(actionType && { actionType }),
      ...(status && { status }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";

    switch (statusLower) {
      case "active":
        bgColor = "bg-green-100";
        textColor = "text-green-800";
        break;
      case "completed":
        bgColor = "bg-blue-100";
        textColor = "text-blue-800";
        break;
      case "paused":
        bgColor = "bg-yellow-100";
        textColor = "text-yellow-800";
        break;
      case "failed":
        bgColor = "bg-red-100";
        textColor = "text-red-800";
        break;
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getActionTypeBadge = (actionType: string) => {
    const formattedType = actionType
      ?.split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        {formattedType}
      </span>
    );
  };

  const handleRerun = (workflowId: string) => {
    const payload = {
      id: workflowId,
    };

    reRunWorkflow.mutateAsync(payload);
  };

  const columns: TableColumn<WorkflowData>[] = [
    {
      key: "name",
      header: "Workflow Name",
      sortable: true,
    },
    {
      key: "audienceTypeDisplay",
      header: "Audience Type",
      sortable: true,
      render: (value: string) => {
        const audience = audienceTypeList.find((a) => a.value === value);
        return (
          <span className="text-sm text-gray-600">
            {audience?.label || value}
          </span>
        );
      },
    },
    {
      key: "actionTypeDisplay",
      header: "Action Type",
      sortable: true,
      render: (value: string) => getActionTypeBadge(value),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: string) => getStatusBadge(value),
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row: any) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "lastModifiedDate",
      header: "Updated On",
      render: (value, row: any) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.lastModifiedUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
  ];

  const canRerun = (row: WorkflowData) => {
    return (
      row.status.toLowerCase() === "completed" &&
      (row.actionType.toLowerCase() === "immediate" ||
        row.actionType.toLowerCase() === "time_based")
    );
  };

  const actions = [
    {
      label: "View",
      onClick: (row: WorkflowData) => {
        navigate(`/communication-hub/workflows/view/${row.id}`);
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Edit",
      onClick: (row: WorkflowData) => {
        navigate(`/communication-hub/workflows/edit/${row.id}`);
      },
      icon: <Edit className="h-4 w-4 mr-2" />,
    },
    {
      label: "Re-run",
      onClick: (row: WorkflowData) => {
        handleRerun(row.id);
        // Add re-run logic here
      },
      icon: <RotateCw className="h-4 w-4 mr-2" />,
      condition: (row: WorkflowData) => canRerun(row),
    },
  ];

  const filterPanel = (
    <WorkflowsFilter
      audienceType={audienceType}
      actionType={actionType}
      status={status}
      audienceTypeList={audienceTypeList}
      actionTypeList={actionTypeList}
      statusList={statusList}
      onApplyFilters={(filters) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          ...(filters.audienceType && { audienceType: filters.audienceType }),
          ...(filters.actionType && { actionType: filters.actionType }),
          ...(filters.status && { status: filters.status }),
        };
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = audienceType || actionType || status;

  return (
    <div className="space-y-6 p-1">
      <GenericTable
        data={workflowData?.results || []}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No workflows found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by workflow name..."
        filterPanel={filterPanel}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {
                  [!!audienceType, !!actionType, !!status].filter(Boolean)
                    .length
                }{" "}
                filter(s) active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default WorkflowsList;
